import type { CsvRow, ValidatedRow, ServerError } from '../types/bulkUpload';

const trimField = (row: CsvRow, field: keyof CsvRow): string =>
  (row[field] || '').trim();

interface HasName {
  first_name?: string;
  last_name?: string;
}

const fullName = (row: HasName): string =>
  `${(row.first_name || '').trim()} ${(row.last_name || '').trim()}`.trim();

const ALL_COLUMNS = [
  'username',
  'first_name',
  'last_name',
  'email',
  'mobile_number',
  'roles',
  'features',
  'receive_queries_from_branches',
  'query_and_leave_approval_emails_enabled',
  'tags',
] as const;

const REQUIRED_COLUMNS = ['first_name', 'last_name', 'email', 'mobile_number'] as const;

const downloadCsv = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const downloadCsvTemplate = (): void => {
  const header = ALL_COLUMNS.join(',');
  // Leading comma = empty username (optional field, email used as fallback)
  const exampleRow = ',John,Smith,john@example.com,27821234567,Admin,Feature1,Branch1,TRUE,Tag1';
  downloadCsv(`${header}\n${exampleRow}`, 'bulk_users_template.csv');
};

const escapeCsvField = (val: string): string => {
  const needsQuoting = val.includes(',') || val.includes('"') || val.includes('\n') || val.includes('\r');
  const hasFormulaPrefix = /^\s*[=+\-@\t\r]/.test(val);

  if (hasFormulaPrefix || needsQuoting) {
    const escaped = val.replace(/"/g, '""');
    return hasFormulaPrefix ? `"\t${escaped}"` : `"${escaped}"`;
  }
  return val;
};

const downloadErrorReport = (errorRows: ValidatedRow[]): void => {
  const header = [...ALL_COLUMNS, 'errors'].join(',');
  const rows = errorRows.map((r) => {
    const values = ALL_COLUMNS.map((col) => escapeCsvField(r.data[col] || ''));
    values.push(escapeCsvField(r.errors.join('; ')));
    return values.join(',');
  });
  downloadCsv([header, ...rows].join('\n'), 'bulk_upload_errors.csv');
};

const formatServerErrors = (errors: ServerError[]): string =>
  errors
    .map((e) => {
      const name = (e.data ? fullName(e.data) : '') || `Row ${e.row}`;
      const username = e.data?.username ? ` (${e.data.username})` : '';
      return `${name}${username} [Row ${e.row}]: ${e.error}`;
    })
    .join('\n');

export { REQUIRED_COLUMNS, trimField, fullName, downloadCsvTemplate, downloadErrorReport, formatServerErrors };
